import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { facilityAdmin, staffQueue } from "../api";
import { Btn } from "../components/ui";
import useQueuePoller from "../hooks/useQueuePoller";
import toast from "react-hot-toast";

const CONVERSATION_POLL_MS = 6000; // same cadence as the full app's conversation panel

// Same accessibility + language reference data as StaffQueuePage.js —
// duplicated rather than imported so the Mini Panel stays a self-contained
// page (it's loaded standalone in an iframe, not through the rest of the
// app's routing/layout). Keep these two in sync if either changes.
const ACCESS_ICONS  = { simple: "📖", hearing: "👂", visual: "👁", mobility: "♿", communication: "💬" };
const ACCESS_LABELS = { simple: "Easy Reading", hearing: "Hearing Impairment", visual: "Visual Impairment", mobility: "Mobility Assistance", communication: "Communication Assistance" };
const ACCESS_COLOR  = "#34D399"; // Mini Panel uses one accent color for all modes to stay compact — full detail (per-mode color/notes) lives in the main app

const LANGUAGE_NAMES  = { tw: "Twi", ga: "Ga", ee: "Ewe", ha: "Hausa", fr: "French", ar: "Arabic", zh: "Chinese" };
const LANGUAGE_ENGINE = { fr: "live", ar: "live", zh: "live" };

function languageBadgeInfo(code) {
  if (!code || code === "en") return null;
  const name = LANGUAGE_NAMES[code] || code.toUpperCase();
  const live = LANGUAGE_ENGINE[code] === "live";
  const tooltip = live
    ? `Speaks ${name} — free-text messages are auto-translated for this patient.`
    : `Speaks ${name} — no live translation engine; only pre-written canned messages in ${name} work. Free-text is sent in English.`;
  return { name, tooltip, live };
}

// Aura Companion — Mini Panel (see docs/adr/ADR-001-aura-companion.md).
// Deliberately narrow: only what staff need to move the queue along
// without leaving their host application's tab. No navigation, no
// reports, no settings — anyone who wants those opens the full AuraQueue
// tab themselves, same as before this feature existed. This page is
// meant to be loaded two ways: directly (for testing) or inside an
// <iframe> opened by the Aura Companion browser extension, which is why
// login lives on this same page rather than redirecting to /login — a
// redirect would navigate the iframe away from a page the host's CSP
// might not have allowed in the first place.
export default function CompanionPage() {
  const { user, login, loading } = useAuth();

  if (loading) return null;
  return (
    <div style={S.root}>
      {!user ? <CompanionLogin onLogin={login} /> : <CompanionQueuePanel />}
    </div>
  );
}

function CompanionLogin({ onLogin }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await onLogin(email, password);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} style={S.loginForm}>
      <div style={S.brand}>AuraQueue</div>
      <div style={S.brandSub}>Sign in to use the queue panel</div>
      <input
        style={S.input} type="email" placeholder="Email" value={email}
        onChange={e => setEmail(e.target.value)} autoComplete="username"
      />
      <input
        style={S.input} type="password" placeholder="Password" value={password}
        onChange={e => setPassword(e.target.value)} autoComplete="current-password"
      />
      {error && <div style={S.error}>{error}</div>}
      <Btn type="submit" disabled={submitting} style={{ width: "100%" }}>
        {submitting ? "Signing in…" : "Sign in"}
      </Btn>
    </form>
  );
}

function CompanionQueuePanel() {
  const { user, logout } = useAuth();
  const { queue, loading, error, refresh } = useQueuePoller();
  const [companionEnabled, setCompanionEnabled] = useState(true); // optimistic; corrected below
  const [checkedFlag, setCheckedFlag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickingOutcome, setPickingOutcome] = useState(false);
  const [myTransfers, setMyTransfers] = useState([]);
  const [recallingId, setRecallingId] = useState(null);

  // Conversation with the currently-served patient — the same
  // translated-messaging feature as the full app's 💬 panel, condensed.
  // This was in ADR-001's scope from the start ("a minimal conversation
  // affordance... since it's core to calling patients through") but
  // hadn't actually been built into the Mini Panel yet — the language
  // badge added earlier flags a barrier exists, this is what staff
  // actually use to do something about it without leaving the panel.
  const [showChat, setShowChat] = useState(false);
  const [chatThread, setChatThread] = useState([]);
  const [chatCanned, setChatCanned] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSending, setChatSending] = useState(false);
  const [chatText, setChatText] = useState("");

  useEffect(() => {
    facilityAdmin.getSettings()
      .then(r => setCompanionEnabled(!!r.data?.companion_enabled))
      .catch(() => {}) // fail open — don't block staff over a settings-read hiccup
      .finally(() => setCheckedFlag(true));
  }, []);

  // "Sent by You — Still Waiting": tickets this staff member personally
  // transferred elsewhere that nobody's picked up yet, with a one-click
  // way to pull them back. Same data/action as the full app's panel of
  // the same name — surfaced here too since recalling a stuck transfer is
  // exactly the kind of quick queue-movement action Companion exists for,
  // not something that should force opening the full tab.
  const loadMyTransfers = useCallback(() => {
    staffQueue.myTransfers().then(r => setMyTransfers(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    loadMyTransfers();
    const interval = setInterval(loadMyTransfers, 10000);
    return () => clearInterval(interval);
  }, [loadMyTransfers]);

  // A new ticket being served (or none at all) should never inherit a
  // picker left open from whichever ticket was up before it. Every hook
  // in this component must run unconditionally on every render (Rules of
  // Hooks) — including this one — so it lives up here above the early
  // returns below, not next to the outcome logic it actually belongs
  // with further down.
  useEffect(() => { setPickingOutcome(false); setShowChat(false); }, [queue?.serving?.id]);

  const servingId = queue?.serving?.id;

  const loadChat = useCallback(() => {
    if (!servingId) return;
    staffQueue.conversation(servingId)
      .then(r => setChatThread(r.data?.thread || []))
      .catch(() => {});
  }, [servingId]);

  useEffect(() => {
    if (!showChat || !servingId) return;
    setChatLoading(true);
    Promise.all([
      staffQueue.conversation(servingId),
      facilityAdmin.listCannedMessages(),
    ])
      .then(([convoRes, cannedRes]) => {
        setChatThread(convoRes.data?.thread || []);
        setChatCanned((cannedRes.data?.results || cannedRes.data).filter(m => m.is_active));
      })
      .catch(() => toast.error("Failed to load conversation."))
      .finally(() => setChatLoading(false));
  }, [showChat, servingId]);

  useEffect(() => {
    if (!showChat || !servingId) return;
    const interval = setInterval(loadChat, CONVERSATION_POLL_MS);
    return () => clearInterval(interval);
  }, [showChat, servingId, loadChat]);

  // Shown on every branch below (disabled facility, error, or the working
  // panel) — this iframe has its own separate login from the main
  // AuraQueue tab and the extension, so a stale or wrong-account session
  // (e.g. someone else's browser profile, or a super admin's own token
  // left over from testing — super admins have no station, which is
  // exactly the "No station assigned." error this exists to get unstuck
  // from) had no way to switch out of itself without going around through
  // the full site. This is that way out.
  const accountBar = (
    <div style={S.accountBar}>
      <span>{user?.email}</span>
      <button style={S.signOutLink} onClick={logout}>Not you? Sign out</button>
    </div>
  );

  if (checkedFlag && !companionEnabled) {
    return (
      <>
        {accountBar}
        <div style={S.disabledNote}>
          Aura Companion isn't enabled for your facility. Ask your super admin to turn it on, or use
          the full AuraQueue tab as usual.
        </div>
      </>
    );
  }

  if (loading && !queue) return <>{accountBar}<div style={S.status}>Loading queue…</div></>;
  if (error) return (
    <>
      {accountBar}
      <div style={S.status}>
        {error}
        {error === "No station assigned." && (
          <div style={{ marginTop: "8px", fontSize: "11px" }}>
            The Mini Panel is for staff accounts with a station assigned — a super admin or
            facility admin account won't have one. Sign out above and sign in as the right staff
            member, or ask your facility admin to assign you a station.
          </div>
        )}
      </div>
    </>
  );

  const serving = queue?.serving;
  const waitingCount = queue?.waiting_count ?? 0;

  const run = async (fn, successMsg) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Action failed.");
    } finally {
      setBusy(false);
    }
  };

  // A station's outcomes decide what "Complete" even means (e.g.
  // Reception has to pick Lab / MRI / X-Ray / Cashier) — the transfer
  // endpoint always requires an outcome_id and 404s without one
  // ("Outcome not found."), so this can't be a single blind button the
  // way it first shipped. One configured outcome skips the picker
  // entirely since there's nothing to choose; more than one shows the
  // compact list below instead of a modal, to stay inside the panel's
  // one-page footprint (see the ADR — no dialogs/nav here by design).
  const outcomes = queue?.outcomes || [];

  // Returning Patients: tickets already waiting at THIS station a second
  // time — either transferred back from elsewhere, or skipped and never
  // called again — surfaced separately from the plain waiting count
  // because "Call Next" alone doesn't make it visible that one of these
  // is sitting there needing direct attention. Same data (queue.returning)
  // and action (call-specific, bypassing normal turn order) as the full
  // app's "Returning Patients" panel.
  const returning = queue?.returning || [];
  const callReturning = (ticket) => {
    run(
      () => staffQueue.callSpecific({ ticket_id: ticket.id, counter_id: queue?.selected_counter?.id || null }),
      `Calling ${ticket.ticket_number}…`
    );
  };

  const handleComplete = () => {
    if (outcomes.length === 0) {
      toast.error("No outcomes configured for this station — set one up in the full AuraQueue tab under Zones & Stations.");
      return;
    }
    if (outcomes.length === 1) {
      run(() => staffQueue.transfer(serving.id, { outcome_id: outcomes[0].id }), "Completed / transferred.");
      return;
    }
    setPickingOutcome(true);
  };

  const chooseOutcome = (outcome) => {
    setPickingOutcome(false);
    run(() => staffQueue.transfer(serving.id, { outcome_id: outcome.id }), `Sent to ${outcome.label_en}.`);
  };

  const recallTransfer = async (ticket) => {
    if (recallingId) return;
    setRecallingId(ticket.id);
    try {
      await staffQueue.recallTicket(ticket.id);
      toast.success(`${ticket.ticket_number} recalled.`);
      loadMyTransfers();
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to recall.");
    } finally {
      setRecallingId(null);
    }
  };

  const langInfo = serving ? languageBadgeInfo(serving.language) : null;

  const sendCanned = async (cannedMessage) => {
    if (!serving || chatSending) return;
    setChatSending(true);
    try {
      const { data } = await staffQueue.sendMessage(serving.id, { canned_message_id: cannedMessage.id });
      setChatThread(t => [...t, { id: data.id, text: data.text_en, from: "staff", sent_by_name: user?.name, sent_at: data.sent_at }]);
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to send message.");
    } finally { setChatSending(false); }
  };

  const sendChatText = async () => {
    const trimmed = chatText.trim();
    if (!serving || !trimmed || chatSending) return;
    setChatSending(true);
    try {
      const { data } = await staffQueue.sendMessage(serving.id, { text: trimmed });
      setChatThread(t => [...t, { id: data.id, text: data.text_en, from: "staff", sent_by_name: user?.name, sent_at: data.sent_at }]);
      setChatText("");
    } catch (e) {
      toast.error(e.response?.data?.error || "Failed to send message.");
    } finally { setChatSending(false); }
  };

  return (
    <div style={S.panel}>
      {accountBar}
      <div style={S.header}>
        <span style={S.headerTitle}>Aura Companion</span>
        <span style={S.waitingPill}>{waitingCount} waiting</span>
      </div>

      {returning.length > 0 && (
        <div style={S.returningCard}>
          <div style={S.returningHeader}>
            <span>↩ Returning Patients</span>
            <span style={S.returningBadge}>{returning.length}</span>
          </div>
          <div style={S.returningHint}>Call directly — skip the normal turn order.</div>
          <div style={S.transfersList}>
            {returning.map(r => (
              <div key={r.id} style={S.transferRow}>
                <div>
                  <div style={S.transferTicket}>
                    {r.ticket_number} <span style={S.transferName}>{r.patient_name}</span>
                  </div>
                  <div style={S.transferAt}>
                    {r.is_skipped
                      ? `⏭ Skipped${r.skip_count > 1 ? ` ×${r.skip_count}` : ""}`
                      : `From: ${r.from_station || "—"}`}
                  </div>
                </div>
                <button style={S.returningCallBtn} disabled={busy} onClick={() => callReturning(r)}>
                  Call ▶
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {serving ? (
        <div style={S.servingCard}>
          <div style={S.servingLabel}>Now Serving</div>
          <div style={S.ticketNum}>{serving.ticket_number}</div>
          <div style={S.patientName}>{serving.patient_name}</div>

          {(serving.accessibility_mode && serving.accessibility_mode !== "none") || langInfo || serving.is_skipped ? (
            <div style={S.badgeRow}>
              {serving.accessibility_mode && serving.accessibility_mode !== "none" && (
                <span style={{ ...S.badge, color: ACCESS_COLOR, borderColor: "rgba(52,211,153,0.35)", background: "rgba(52,211,153,0.1)" }}
                  title={ACCESS_LABELS[serving.accessibility_mode]}>
                  {ACCESS_ICONS[serving.accessibility_mode]} {ACCESS_LABELS[serving.accessibility_mode]}
                </span>
              )}
              {langInfo && (
                <span style={{ ...S.badge, color: langInfo.live ? "#60A5FA" : "#F59E0B", borderColor: langInfo.live ? "rgba(96,165,250,0.35)" : "rgba(245,158,11,0.35)", background: langInfo.live ? "rgba(96,165,250,0.1)" : "rgba(245,158,11,0.1)" }}
                  title={langInfo.tooltip}>
                  🌐 {langInfo.name}
                </span>
              )}
              {serving.is_skipped && (
                <span style={{ ...S.badge, color: "#EF4444", borderColor: "rgba(239,68,68,0.35)", background: "rgba(239,68,68,0.1)" }}
                  title={`Skipped ${serving.skip_count} time${serving.skip_count === 1 ? "" : "s"} before being called again`}>
                  ⏭ Skipped{serving.skip_count > 1 ? ` ×${serving.skip_count}` : ""}
                </span>
              )}
            </div>
          ) : null}

          {showChat ? (
            <div style={S.chatWrap}>
              {chatLoading ? <div style={S.chatLoading}>Loading conversation…</div> : (
                <>
                  {chatThread.length > 0 && (
                    <div style={S.chatThread}>
                      {chatThread.map((m, i) => (
                        <div key={m.id || i} style={{
                          ...S.chatBubble,
                          alignSelf: m.from === "patient" ? "flex-start" : "flex-end",
                          background: m.from === "patient" ? "rgba(52,211,153,0.12)" : "rgba(37,99,235,0.15)",
                          borderColor: m.from === "patient" ? "rgba(52,211,153,0.3)" : "rgba(37,99,235,0.3)",
                        }}>
                          <div style={S.chatFrom}>
                            {m.from === "patient" ? "Patient" : (m.sent_by_name || "Staff")}
                            {m.translated && " · translated"}
                          </div>
                          {m.text}
                          {m.original_text && <div style={S.chatOriginal}>"{m.original_text}"</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  <input
                    style={S.chatInput} value={chatText} placeholder="Type a message…" maxLength={300}
                    disabled={chatSending}
                    onChange={e => setChatText(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") sendChatText(); }}
                  />
                  <div style={S.chatLangNote}>
                    {langInfo?.tooltip || "Sent in English — this patient's ticket language is English."}
                  </div>
                  {chatCanned.length > 0 && (
                    <div style={S.chatCannedList}>
                      {chatCanned.map(m => (
                        <button key={m.id} style={S.chatCannedBtn} disabled={chatSending} onClick={() => sendCanned(m)}>
                          {m.text_en}
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={S.chatActions}>
                    <button style={S.outcomeCancel} onClick={() => setShowChat(false)}>← Back</button>
                    <Btn size="sm" onClick={sendChatText} disabled={chatSending || !chatText.trim()}>Send</Btn>
                  </div>
                </>
              )}
            </div>
          ) : pickingOutcome ? (
            <div style={S.outcomeList}>
              <div style={S.outcomeHint}>Where does this patient go next?</div>
              {outcomes.map(o => (
                <button key={o.id} style={S.outcomeBtn} disabled={busy} onClick={() => chooseOutcome(o)}>
                  <span>{o.label_en}</span>
                  {o.next_station__name && <span style={S.outcomeSub}>→ {o.next_station__name}</span>}
                </button>
              ))}
              <button style={S.outcomeCancel} onClick={() => setPickingOutcome(false)} disabled={busy}>Cancel</button>
            </div>
          ) : (
            <>
              <div style={S.actionsGrid}>
                <Btn size="sm" onClick={handleComplete} disabled={busy}>
                  Complete →
                </Btn>
                <Btn size="sm" variant="ghost" onClick={() => run(() => staffQueue.reAnnounce(serving.id), "Re-announced.")} disabled={busy}>
                  Reannounce 📣
                </Btn>
                <Btn size="sm" variant="ghost" onClick={() => run(() => staffQueue.skip(serving.id), "Skipped.")} disabled={busy}
                  style={{ color: "#EF4444", borderColor: "rgba(239,68,68,0.3)" }}>
                  Skip
                </Btn>
                <Btn size="sm" variant="ghost" onClick={() => run(() => staffQueue.hold(serving.id), "On hold.")} disabled={busy}
                  style={{ color: "#F59E0B", borderColor: "rgba(245,158,11,0.3)" }}>
                  Hold
                </Btn>
              </div>
              <button style={S.chatToggleBtn} onClick={() => setShowChat(true)}>
                💬 Message patient
                {serving.unread_reply_count > 0 && <span style={S.chatUnreadDot} />}
              </button>
            </>
          )}
        </div>
      ) : (
        <div style={S.idleCard}>
          <div style={S.idleText}>{waitingCount > 0 ? `${waitingCount} patient${waitingCount !== 1 ? "s" : ""} waiting` : "Queue is empty"}</div>
          <Btn onClick={() => run(() => staffQueue.callNext({}), "Calling next…")} disabled={busy || waitingCount === 0} style={{ width: "100%" }}>
            {waitingCount > 0 ? "Call Next ▶" : "No one waiting"}
          </Btn>
        </div>
      )}

      {myTransfers.length > 0 && (
        <div style={S.transfersCard}>
          <div style={S.transfersHeader}>
            <span>Sent by You — Still Waiting</span>
            <span style={S.waitingPill}>{myTransfers.length}</span>
          </div>
          <div style={S.transfersList}>
            {myTransfers.map(t => (
              <div key={t.id} style={S.transferRow}>
                <div>
                  <div style={S.transferTicket}>{t.ticket_number} <span style={S.transferName}>{t.patient_name}</span></div>
                  <div style={S.transferAt}>At: {t.current_station_name || "—"}</div>
                </div>
                <button style={S.transferRecallBtn} disabled={recallingId === t.id} onClick={() => recallTransfer(t)}>
                  {recallingId === t.id ? "…" : "Recall ↺"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={S.footerNote}>
        Need reports or settings? Open the full AuraQueue tab.
      </div>
    </div>
  );
}

const S = {
  root: {
    fontFamily: "'DM Sans','Segoe UI',sans-serif", background: "#0B1220", color: "#F1F5F9",
    minHeight: "100vh", boxSizing: "border-box", padding: "16px",
  },
  loginForm: { display: "flex", flexDirection: "column", gap: "10px", maxWidth: "260px", margin: "40px auto" },
  brand: { fontSize: "16px", fontWeight: "700", textAlign: "center" },
  brandSub: { fontSize: "12px", color: "#94A3B8", textAlign: "center", marginBottom: "8px" },
  input: {
    background: "#111A2E", border: "1px solid #22304A", borderRadius: "8px",
    padding: "9px 12px", color: "#F1F5F9", fontSize: "13px", outline: "none", fontFamily: "inherit",
  },
  error: { fontSize: "11px", color: "#EF4444" },
  status: { textAlign: "center", padding: "40px 16px", fontSize: "13px", color: "#94A3B8" },
  accountBar: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px",
    fontSize: "10px", color: "#64748B", paddingBottom: "8px", marginBottom: "4px",
    borderBottom: "1px solid #1B2942",
  },
  signOutLink: {
    background: "none", border: "none", color: "#60A5FA", fontSize: "10px",
    cursor: "pointer", fontFamily: "inherit", padding: 0, textDecoration: "underline",
  },
  disabledNote: { padding: "24px 16px", fontSize: "12px", color: "#94A3B8", textAlign: "center", lineHeight: 1.6 },
  panel: { display: "flex", flexDirection: "column", gap: "12px" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between" },
  headerTitle: { fontSize: "13px", fontWeight: "700", letterSpacing: "0.04em" },
  waitingPill: {
    fontSize: "11px", fontWeight: "700", background: "rgba(37,99,235,0.15)", color: "#60A5FA",
    border: "1px solid rgba(37,99,235,0.3)", borderRadius: "999px", padding: "3px 10px",
  },
  servingCard: {
    background: "#111A2E", border: "1px solid #22304A", borderRadius: "12px", padding: "14px",
    display: "flex", flexDirection: "column", gap: "6px",
  },
  servingLabel: { fontSize: "10px", fontWeight: "700", color: "#60A5FA", letterSpacing: "0.08em", textTransform: "uppercase" },
  ticketNum: { fontSize: "26px", fontWeight: "800" },
  patientName: { fontSize: "13px", color: "#CBD5E1", marginBottom: "6px" },
  actionsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" },
  badgeRow: { display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "2px" },
  badge: {
    display: "inline-flex", alignItems: "center", gap: "3px",
    fontSize: "10px", fontWeight: "700", borderRadius: "6px",
    padding: "2px 7px", border: "1px solid", cursor: "help",
  },
  returningCard: {
    background: "rgba(249,115,22,0.08)", border: "1px solid rgba(249,115,22,0.3)", borderRadius: "12px",
    padding: "12px 14px", display: "flex", flexDirection: "column", gap: "8px",
  },
  returningHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    fontSize: "11.5px", fontWeight: "700", color: "#F97316",
  },
  returningBadge: {
    background: "rgba(249,115,22,0.2)", color: "#F97316", borderRadius: "999px",
    fontSize: "10px", fontWeight: "700", padding: "1px 8px",
  },
  returningHint: { fontSize: "10.5px", color: "#94A3B8" },
  returningCallBtn: {
    background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.35)", color: "#F97316",
    borderRadius: "7px", padding: "5px 10px", fontSize: "11px", fontWeight: "700",
    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
  },
  transfersCard: {
    background: "#111A2E", border: "1px solid #22304A", borderRadius: "12px", padding: "12px 14px",
    display: "flex", flexDirection: "column", gap: "10px",
  },
  transfersHeader: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    fontSize: "11px", fontWeight: "700", color: "#A78BFA",
  },
  transfersList: { display: "flex", flexDirection: "column", gap: "8px" },
  transferRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px",
    background: "#17223B", border: "1px solid #22304A", borderRadius: "8px", padding: "8px 10px",
  },
  transferTicket: { fontSize: "12.5px", fontWeight: "700" },
  transferName: { fontSize: "11px", fontWeight: "500", color: "#94A3B8", marginLeft: "6px" },
  transferAt: { fontSize: "10.5px", color: "#64748B", marginTop: "2px" },
  transferRecallBtn: {
    background: "rgba(167,139,250,0.15)", border: "1px solid rgba(167,139,250,0.35)", color: "#A78BFA",
    borderRadius: "7px", padding: "5px 10px", fontSize: "11px", fontWeight: "700",
    cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
  },
  outcomeList: { display: "flex", flexDirection: "column", gap: "6px" },
  outcomeHint: { fontSize: "11px", color: "#94A3B8", marginBottom: "2px" },
  outcomeBtn: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: "#17223B", border: "1px solid #22304A", borderRadius: "8px",
    padding: "9px 12px", color: "#F1F5F9", fontSize: "12.5px", fontWeight: "600",
    cursor: "pointer", fontFamily: "inherit", textAlign: "left",
  },
  outcomeSub: { fontSize: "11px", fontWeight: "500", color: "#94A3B8" },
  outcomeCancel: {
    background: "none", border: "none", color: "#94A3B8", fontSize: "11.5px",
    cursor: "pointer", fontFamily: "inherit", padding: "4px 0", textAlign: "center",
  },
  chatToggleBtn: {
    display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
    background: "none", border: "1px solid #22304A", borderRadius: "8px",
    color: "#94A3B8", fontSize: "11.5px", fontWeight: "600", padding: "8px",
    cursor: "pointer", fontFamily: "inherit", position: "relative",
  },
  chatUnreadDot: {
    width: "7px", height: "7px", borderRadius: "50%", background: "#EF4444",
  },
  chatWrap: { display: "flex", flexDirection: "column", gap: "8px" },
  chatLoading: { fontSize: "11px", color: "#94A3B8", textAlign: "center", padding: "12px 0" },
  chatThread: { display: "flex", flexDirection: "column", gap: "6px", maxHeight: "150px", overflowY: "auto" },
  chatBubble: {
    maxWidth: "85%", padding: "7px 10px", borderRadius: "9px", fontSize: "12px", lineHeight: 1.4,
    border: "1px solid", color: "#F1F5F9", display: "flex", flexDirection: "column",
  },
  chatFrom: { fontSize: "9px", fontWeight: "700", opacity: 0.7, marginBottom: "2px", textTransform: "uppercase" },
  chatOriginal: { fontSize: "10.5px", opacity: 0.6, marginTop: "3px", fontStyle: "italic" },
  chatInput: {
    background: "#111A2E", border: "1px solid #22304A", borderRadius: "8px",
    padding: "8px 10px", color: "#F1F5F9", fontSize: "12.5px", outline: "none", fontFamily: "inherit",
  },
  chatLangNote: { fontSize: "10.5px", color: "#94A3B8" },
  chatCannedList: { display: "flex", flexDirection: "column", gap: "6px", maxHeight: "120px", overflowY: "auto" },
  chatCannedBtn: {
    textAlign: "left", background: "#17223B", border: "1px solid #22304A", borderRadius: "8px",
    padding: "8px 10px", color: "#F1F5F9", fontSize: "12px", fontWeight: "500",
    cursor: "pointer", fontFamily: "inherit",
  },
  chatActions: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "2px" },
  idleCard: {
    background: "#111A2E", border: "1px solid #22304A", borderRadius: "12px", padding: "18px",
    display: "flex", flexDirection: "column", gap: "12px", alignItems: "center", textAlign: "center",
  },
  idleText: { fontSize: "13px", color: "#94A3B8" },
  footerNote: { fontSize: "10px", color: "#64748B", textAlign: "center" },
};
